import React, { useEffect, useState, useCallback, ReactNode, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { ReactComponent as CloseIcon } from "../../assets/x-close-white.svg";
import * as Popover from "@radix-ui/react-popover";

interface FeatureHintProps {
    id: string;
    side: "bottom" | "left" | "top" | "right";
    align: "end" | "start" | "center";
    trigger: ReactNode;
    title: ReactNode;
    description: ReactNode;
}

export const FeatureHint: React.FC<FeatureHintProps> = ({
    id,
    side,
    align,
    trigger,
    title,
    description,
}) => {
    const storageKey = `feature-hint-hidden:${id}`;
    const sessionKey = `feature-hint-session-hidden:${id}`;
    const [visible, setVisible] = useState(false);
    const gotItButtonRef = useRef<HTMLButtonElement>(null);

    const canShow = useCallback(() => {
        const dismissed = localStorage.getItem(storageKey);
        const hiddenThisSession = sessionStorage.getItem(sessionKey);
        return !dismissed && !hiddenThisSession;
    }, [storageKey, sessionKey]);

    useEffect(() => {
        if (canShow()) {
            setVisible(true);
            window.dataLayer.push({
                "event": "cl_hint_open",
            });
        }
    }, [canShow]);

    const handleClose = useCallback((type: "permanent" | "session") => {
        if (!visible) return;

        if (type === "permanent") {
            localStorage.setItem(storageKey, "true");
        } else if (type === "session") {
            sessionStorage.setItem(sessionKey, "true");
        }

        window.dispatchEvent(new Event("featureHintChange"));
        setVisible(false);
    }, [visible, storageKey, sessionKey]);

    const handleMaybeLater = () => {
        handleClose("session");
        window.dataLayer.push({ event: "cl_hint_maybe_later_click" });
    };

    const handleEscapeKeyDown = (e: KeyboardEvent) => {
        handleClose("permanent");
        window.dataLayer.push({ event: "cl_hint_esc_click" });
    };

    const handleCloseOrGotIconClick = () => {
        handleClose("permanent");
        window.dataLayer.push({ event: "cl_hint_x_click" });
    };

    // Handler for Popover's onOpenChange - only responds to external close triggers
    const handleOpenChange = (open: boolean) => {
        if (!open && visible) {
            // When Popover closes externally (e.g., clicking outside), just close without saving
            window.dispatchEvent(new Event("featureHintChange"));
            setVisible(false);
        }
    };

    return (
        <Popover.Root open={visible} onOpenChange={handleOpenChange}>
            <Popover.Anchor asChild>{trigger}</Popover.Anchor>
            {visible && (
                <Popover.Portal>
                    <Popover.Content
                        side={side}
                        align={align}
                        sideOffset={10}
                        className="feature-hint"
                        onOpenAutoFocus={(e) => {
                            e.preventDefault();
                            gotItButtonRef.current?.focus();
                        }}
                        onEscapeKeyDown={handleEscapeKeyDown}
                    >
                        <div className="feature-hint__header">
                            {title}
                            <CloseIcon
                                onClick={handleCloseOrGotIconClick}
                                style={{ cursor: "pointer" }}
                            />
                        </div>
                        <div className="feature-hint__body feature-hint-message">
                            {description}
                        </div>
                        <div className="feature-hint__footer">
                            <button
                                className="button button-secondary button-medium feature-hint__button"
                                onClick={handleMaybeLater}
                            >
                                <FormattedMessage id="MaybeLater" defaultMessage="Maybe later" />
                            </button>
                            <button
                                ref={gotItButtonRef}
                                className="button button-primary button-medium feature-hint__button"
                                onClick={handleCloseOrGotIconClick}
                            >
                                <FormattedMessage id="GotIt" defaultMessage="Got it" />
                            </button>
                        </div>
                        <Popover.Arrow height={10} width={15} />
                    </Popover.Content>
                </Popover.Portal>
            )}
        </Popover.Root>
    );
};